import steConversion from "slate-transcript-editor/util/dpe-to-slate/index";

import { fixParagraph } from "./story_authoring_align";
import type { DpeDocument, DpeParagraph, DpeWord, SlateDocument, SlateParagraph, STTWord } from "./types";

function validDpeParagraph(para: DpeParagraph): boolean {
  return typeof para.start === "number" && typeof para.end === "number" && typeof para.speaker === "string";
}

// For now, this function just exists to apply some pre-emptive filtering to
// slate-transcript-editor's code. At this point though, we might as well just
// write our own.
export function convertDpeToSlate(dpe: DpeDocument): SlateParagraph[] {
  let lastParagraph: typeof dpe.paragraphs[number] = null;
  const filteredParagraphs = dpe.paragraphs.filter(validDpeParagraph).map((p) => {
    const start = lastParagraph && p.start < lastParagraph.end ? lastParagraph.end : p.start;
    const end = start < p.end ? p.end : start + 0.01;
    lastParagraph = { ...p, start, end };
    return lastParagraph;
  });
  let lastWord: typeof dpe.words[number] = null;
  const filteredWords = dpe.words.map((w) => {
    const start = lastWord?.end != null && w.start < lastWord?.end ? lastWord.end : w.start;
    const end = start > w.end ? start + 0.01 : w.end;
    lastWord = { start, end, text: w.text };
    return lastWord;
  });

  const slate: SlateParagraph[] = steConversion({
    ...dpe,
    paragraphs: filteredParagraphs,
    words: filteredWords,
  }) as SlateParagraph[];

  return slate.map((block, idx, arr) =>
    fixParagraph({
      paragraph: block,
      nextParagraph: arr[idx + 1],
    })
  );
}

const toTimeString = (time: number) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  const toStr = (num: number) => num.toString().padStart(2, "0");
  return `${toStr(hours)}:${toStr(minutes)}:${toStr(seconds)}`;
};

// ////////////////////////////////////////////////////////////////////////////////////////////
//
// A bunch of the code below is yet another case were we "borrowed" code from the backend
// package (cli trans) because we still haven't established best way to share things.
//
// ////////////////////////////////////////////////////////////////////////////////////////////

function* validateTimedItemList(items: DpeWord[] | DpeParagraph[]): Generator<[number, string, number?]> {
  const errorCounts = {
    missingTiming: 0,
    outOfTimeOrder: 0,
  };

  for (let idx = 0; idx < items.length; idx++) {
    const item = items[idx];
    if (item.start == null || item.end == null) {
      if (errorCounts.missingTiming < 10) {
        yield [idx, `MISSING${item.start == null ? " StartTime" : ""}${item.end == null ? " EndTime" : ""}`];
        errorCounts.missingTiming += 1;
      }
    }

    if (item.start > item.end) {
      if (errorCounts.outOfTimeOrder < 10) {
        yield [
          idx,
          `Start > End (${item.start} > ${item.end}; ${toTimeString(item.start)} > ${toTimeString(item.end)})`,
        ];
      }
      errorCounts.outOfTimeOrder += 1;
    } else if (idx > 0) {
      const previtem = items[idx - 1];
      if (item.start < previtem.end) {
        if (errorCounts.outOfTimeOrder < 10) {
          yield [
            idx,
            `Start < Prev.End (${item.start} < ${previtem.end}; ${toTimeString(item.start)} < ${toTimeString(
              previtem.end
            )})`,
          ];
        }
        errorCounts.outOfTimeOrder += 1;
      }
    }
  }

  if (errorCounts.missingTiming >= 10) {
    yield [null, "MISSING TIMING INFO", errorCounts.missingTiming];
  }
  if (errorCounts.outOfTimeOrder >= 10) {
    yield [null, "OUT OF ORDER TIMING", errorCounts.outOfTimeOrder];
  }
}

function* validateParagraphList(paragraphs: DpeParagraph[]) {
  for (const [idx, err, num] of validateTimedItemList(paragraphs)) {
    if (idx != null) {
      yield `Paragraph[${idx}] ${err}`;
    } else {
      yield `Total ${num} paragraphs ${err}`;
    }
  }
}

function* validateWordList(words: DpeWord[]) {
  for (const [idx, err, num] of validateTimedItemList(words)) {
    if (idx != null) {
      yield `Word[${idx}] '${words[idx].text}' ${err}`;
    } else {
      yield `Total ${num} words ${err}`;
    }
  }
}

function* validateTranscript(doc: DpeDocument): Generator<string> {
  for (const err of validateParagraphList(doc.paragraphs)) {
    yield err;
  }
  for (const err of validateWordList(doc.words)) {
    yield err;
  }
}

export function checkDpeTranscriptDoc(doc: DpeDocument): boolean {
  let isOkay = true;
  for (const err of validateTranscript(doc)) {
    console.log(`    ${err}`);
    isOkay = false;
  }
  return isOkay;
}

/**
 * Prepares aligned slate content for `convertSlateToDpe`.
 *
 * `convertSlateToDpe` slices the flat word list per paragraph using
 * `countWords(children[0].text)`. If a word's `text` carries internal
 * whitespace (or is empty), that count drifts from the actual `words`
 * length, so a later paragraph slices an empty window and the adapter
 * throws on `blockEntites[0].start` — silently aborting the save before
 * `POST /transcripts/:id/edit` is ever sent.
 *
 * We rebuild each paragraph so every token is a single whitespace-free
 * unit (guaranteeing `countWords(text) === words.length`), drop empty
 * paragraphs, and clamp word timings to be non-decreasing and never
 * inverted — matching `convertDpeToSlate`'s load-time normalization.
 */
export function sanitizeSlateForDpe(slate: SlateDocument): SlateDocument {
  let prevEnd: number | null = null;
  const paragraphs: SlateParagraph[] = [];

  for (const paragraph of slate) {
    const words: STTWord[] = [];
    for (const child of paragraph.children ?? []) {
      for (const word of child.words ?? []) {
        const text = (word.text ?? "").replace(/\s+/g, "").trim();
        if (!text) continue;

        let start: number = Number.isFinite(word.start) ? (word.start as number) : prevEnd ?? 0;
        if (prevEnd != null && start < prevEnd) start = prevEnd;
        let end: number = Number.isFinite(word.end) ? (word.end as number) : start;
        if (start > end) end = start + 0.01;
        prevEnd = end;

        words.push({ start, end, text });
      }
    }

    if (words.length === 0) continue;

    paragraphs.push({
      ...paragraph,
      start: words[0].start ?? 0,
      children: [{ text: words.map((w) => w.text).join(" "), words }],
    });
  }

  return paragraphs;
}
